import React from "react";
import { ClientContext } from "@app/admin/contexts/ClientContext";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { Toggle } from "@app/shared/Toggle";
import { CloudProviderEnum, RegionsByCloudProvider, SnowflakeEditionEnum } from "@app/data-share";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { useGetClientDataShareStatus } from "@app/data-share/hooks/useGetClientDataShareStatus";

const listingTextMaxLength = 110;
const connectionDetailsHint = "Add Organization Name, Account Name, Cloud Provider and Region first.";

interface ClientDataSharingProps {
  disabled?: boolean;
}

export const ClientDataSharing: React.FC<React.PropsWithChildren<ClientDataSharingProps>> = (props: ClientDataSharingProps) => {
  const clientContext = React.useContext(ClientContext);

  // The save endpoint rejects a config missing any of these four, so everything below them stays
  // disabled until they are filled in -- otherwise the edit is accepted and then silently dropped.
  const hasRequiredConnectionDetails = !!(
    clientContext.dataShareOrgName?.trim()
    && clientContext.dataShareAccountName?.trim()
    && clientContext.dataShareRegion?.trim()
    && clientContext.dataShareCloudProvider?.trim()
  );
  const metadataDisabled = props.disabled || !hasRequiredConnectionDetails;

  const isDataShareConfigInUse = !!(
    clientContext.dataShareOrgName?.trim()
    || clientContext.dataShareAccountName?.trim()
    || clientContext.dataShareRegion?.trim()
    || clientContext.dataShareCloudProvider?.trim()
  );

  const dataShareStatusQuery = useGetClientDataShareStatus(clientContext.clientId, isDataShareConfigInUse);
  const showNotProvisionedBanner = dataShareStatusQuery.isSuccess && !dataShareStatusQuery.data.isDatabaseProvisioned;
  const isCheckingDatabaseStatus = !clientContext.dataShareIsActive && dataShareStatusQuery.isLoading;

  return (
    <div className="client-data-sharing">
      <h5 className="data-sharing-section-title col-6">Data Sharing Information</h5>
      { showNotProvisionedBanner &&
        <QESAlert type={"warning"} persist={true} children={"This client's outbound database has not been provisioned or cannot be reached. An outbound database must exist before a data share can be functional."}/>
      }
      { dataShareStatusQuery.isError &&
        <QESAlert type={"warning"} persist={true} children={"Unable to check whether this client's outbound database has been provisioned. An outbound database must exist before a data share can be functional."}/>
      }
      <QESTextInput
        containerClassName="mb-3 col-6"
        label="Organization Name"
        testId="client-data-sharing__org-name"
        value={clientContext.dataShareOrgName}
        onChange={(e) => clientContext.setDataShareOrgName(e.target.value)}
        disabled={props.disabled}
        required={isDataShareConfigInUse}
        isValid={clientContext.validationObject["dataShareConfig"]}
      />
      <QESTextInput
        containerClassName="mb-3 col-6"
        label="Account Name"
        testId="client-data-sharing__account-name"
        value={clientContext.dataShareAccountName}
        onChange={(e) => clientContext.setDataShareAccountName(e.target.value)}
        disabled={props.disabled}
        required={isDataShareConfigInUse}
        isValid={clientContext.validationObject["dataShareConfig"]}
      />
      <QESSelect
        containerClassName="mb-3 col-6"
        label="Cloud Provider"
        testId="client-data-sharing__cloud-provider"
        value={clientContext.dataShareCloudProvider}
        onChange={(e) => {
          clientContext.setDataShareCloudProvider(e.target.value);
          clientContext.setDataShareRegion("");
        }}
        disabled={props.disabled}
        required={isDataShareConfigInUse}
        isValid={clientContext.validationObject["dataShareConfig"]}
        allowEmpty={true}
        placeholder="Select a Cloud Provider:"
      >
        {Object.values(CloudProviderEnum).map((opt, index) =>
          <option key={index} value={opt}>{opt}</option>
        )}
      </QESSelect>
      <QESSelect
        containerClassName="mb-3 col-6"
        label="Region"
        testId="client-data-sharing__region"
        value={clientContext.dataShareRegion}
        onChange={(e) => clientContext.setDataShareRegion(e.target.value)}
        disabled={props.disabled || !clientContext.dataShareCloudProvider}
        required={isDataShareConfigInUse}
        isValid={clientContext.validationObject["dataShareConfig"]}
        allowEmpty={true}
        placeholder="Select a Region:"
      >
        {(RegionsByCloudProvider[clientContext.dataShareCloudProvider as CloudProviderEnum] || []).map((opt, index) =>
          <option key={index} value={opt}>{opt}</option>
        )}
      </QESSelect>
      <QESTextInput
        containerClassName="mb-3 col-6"
        label="Listing Title"
        testId="client-data-sharing__listing-title"
        value={clientContext.dataShareListingTitle}
        onChange={(e) => clientContext.setDataShareListingTitle(e.target.value)}
        disabled={metadataDisabled}
        descriptiveText={metadataDisabled ? connectionDetailsHint : undefined}
        maxLength={listingTextMaxLength}
      />
      <QESTextInput
        containerClassName="mb-3 col-6"
        label="Listing Subtitle"
        testId="client-data-sharing__listing-subtitle"
        value={clientContext.dataShareListingSubtitle}
        onChange={(e) => clientContext.setDataShareListingSubtitle(e.target.value)}
        disabled={metadataDisabled}
        descriptiveText={metadataDisabled ? connectionDetailsHint : undefined}
        maxLength={listingTextMaxLength}
      />
      <QESSelect
        containerClassName="mb-3 col-6"
        label="Snowflake Edition"
        testId="client-data-sharing__snowflake-edition"
        value={clientContext.dataShareSnowflakeEdition}
        onChange={(e) => clientContext.setDataShareSnowflakeEdition(e.target.value)}
        disabled={metadataDisabled}
        descriptiveText={metadataDisabled ? connectionDetailsHint : undefined}
        allowEmpty={true}
        placeholder="Select a Snowflake Edition"
      >
        {Object.values(SnowflakeEditionEnum).map((opt, index) =>
          <option key={index} value={opt}>{opt}</option>
        )}
      </QESSelect>
      <Toggle
        containerClassName="mb-3"
        label="HIPAA BAA Signed"
        testId="client-data-sharing__is-hipaa-baa-signed"
        active={clientContext.dataShareIsHipaaBaaSigned}
        onChange={(wasSigned) => clientContext.setDataShareIsHipaaBaaSigned(!wasSigned)}
        disabled={metadataDisabled}
        size="medium"
      />
      <Toggle
        containerClassName="mb-3"
        label={isCheckingDatabaseStatus ? "Data Share Active (checking outbound database...)" : "Data Share Active"}
        testId="client-data-sharing__is-active"
        active={clientContext.dataShareIsActive}
        onChange={(wasActive) => clientContext.setDataShareIsActive(!wasActive)}
        disabled={metadataDisabled || isCheckingDatabaseStatus}
        size="medium"
      />
    </div>
  );
}
